/** /zh/use-cases/pm-roadmap — Chinese version. */
import { MarketingShell } from '../MarketingShell';

export function PmRoadmapZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/use-cases/pm-roadmap"
      documentTitle="使用场景：PM 季度规划 · Draftlize"
      documentDescription="PM 怎么在 Draftlize 里跑季度 roadmap。从初始头脑风暴到决策到依赖 spec——substrate 优先的 PM 工作流的具体走过。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">使用场景 · PM 季度规划</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            PM 怎么规划 roadmap<br/>
            <em>而不丢失理由。</em>
          </h1>
          <p className="lede">
            具体走一遍：季度 roadmap 规划从白板到可发版 spec，AI agent 干一半活，决策在整个周期里都活着。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">第 1 周 · <em>substrate 是什么？</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">从意图开始</h3>
            <p>打开 Draftlize 建新 project（"Q3 roadmap"）。开个 thread 写："Q3 我想围绕 retention、monetization、onboarding velocity 三个主题规划。帮我想想哪个该重。"</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">agent 抽取卡片</h3>
            <p>来回讨论的时候，agent 对关键想法调 <code>write_card</code>：<code>c-01 statement: retention 是先导指标</code>、<code>c-02 question: monetization 该等 retention 吗？</code>等。你看着它们在卡片栏实时落。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">决定</h3>
            <p>第 1 周末你有 ~30 卡。agent 帮你把关键的提成 <code>decision</code>：<code>c-12 decision: Q3 优先 retention，Q4 优先 monetization</code>。这现在永远可寻址、可引用。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">第 4 周 · <em>决策变了。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">用户调研出现意外</h3>
            <p>你发现 monetization 才是约束，不是 retention。你编辑 <code>c-12</code>："Q3 优先 monetization，Q4 优先 retention。"</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">substrate 标记所有 stale</h3>
            <p>stale 传播反向走 <code>→depends-on c-12</code>。17 张卡被标记：retention OKR、sprint 计划、招聘需求、营销文案草稿。你看到划线 + 琥珀色条。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">有意识地更新或删除</h3>
            <p>逐张过被标记的卡。有些仍然有效（只需重新断言）。有些需要重写。少数被删。周五下班 substrate 又一致了——不是在 demo 当天发现整季度文档错位。</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">第 8 周 · <em>agent 自动读你过去的决策。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">新对话，无需重新解释</h3>
            <p>你开新 thread 设计 monetization 升级流程。agent 把 <code>c-12</code>（优先级决策）、<code>c-19</code>（定价策略）、<code>c-22</code>（目标人群）作为 ambient context 读入——从"既然 Q3 焦点是 monetization 且目标是中端，这里有 3 种升级流程模式可考虑"开始。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">跨 thread 冲突检测</h3>
            <p>你在某 thread 里说"$30/月企业版"。上周在另一个 thread 里定了"$60 Power 档"。Draftlize 把这标成 <code>gap_kind=conflict</code> 卡，两个来源 thread 都链接上。你在发任何一个之前都能看到。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Spec 准备好升级到 Linear</h3>
            <p>卡片够具体可发版时，导出到 Linear（markdown 复制，或粘卡片 URL）。发版后标 <code>built</code>。之后再编辑——状态自动翻 <code>drift_detected</code>。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          第 12 周你做了 ~80 个决策、~200 个 statement、解决了 ~50 个 question。全部可寻址、全部 linked、全部下个季度规划 Q4 时 AI agent 还能读得到。
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">试一个 <em>sprint。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            注册送的 $5 大约够一个 sprint 的思考量——足够感受一次决策反转加 stale 传播。这通常就是 PM 意识到这不只是更好笔记的时刻。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
